/**
 * @fileoverview Syndicator Showdown — Notification Toast Host
 * @module components/notifications/NotificationToastHost
 *
 * Global singleton, mounted once inside AppShell so it is present on every
 * logged-in page. Listens to the current user's unread notifications and
 * surfaces the newest one as a premium NotificationCard toast, queueing the
 * rest. Never renders on top of a gameplay modal (BuyModal / Stable Slip /
 * Share QR) — see useBlockingModalOpen.
 *
 * This phase only *displays* notification docs that already exist in
 * Firestore. It does not create them — see functions/scripts/createTestNotification.js
 * for a dev-only way to seed test docs, and functions/src/announcements.js
 * for the existing welcome/broadcast writers.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNotificationFeed } from '../../hooks/useNotificationFeed';
import { useBlockingModalOpen } from '../../hooks/useBlockingModalOpen';
import NotificationCard from './NotificationCard';
import './NotificationToastHost.css';

const MAX_QUEUE = 5;

export default function NotificationToastHost({ user }) {
  const navigate            = useNavigate();
  const { unreadItems }     = useNotificationFeed(user, { limit: 30 });
  const isModalBlocking     = useBlockingModalOpen();
  const [activeId, setActiveId] = useState(null);
  const laterHiddenRef      = useRef(new Set());

  // Newest-first queue, capped, minus anything the player already said
  // "Later" to this session (silently hidden, not written to Firestore so
  // it can resurface on a future page load / session).
  const queue = useMemo(() => (
    unreadItems.filter((n) => !laterHiddenRef.current.has(n.id)).slice(0, MAX_QUEUE)
  ), [unreadItems]);

  useEffect(() => {
    if (isModalBlocking) return;
    if (activeId && queue.some((n) => n.id === activeId)) return;
    setActiveId(queue[0]?.id ?? null);
  }, [queue, isModalBlocking, activeId]);

  const active = activeId ? queue.find((n) => n.id === activeId) : null;

  async function updateNotif(fields) {
    if (!user || !active) return;
    try {
      await updateDoc(doc(db, 'notifications', user.uid, 'items', active.id), fields);
    } catch (e) {
      console.error('[NotificationToastHost] update error:', e.message);
    }
  }

  async function handleDismiss() {
    setActiveId(null);
    await updateNotif({ status: 'dismissed', dismissedAt: serverTimestamp(), read: true });
  }

  async function handleCta() {
    const ctaUrl = active?.ctaUrl;
    setActiveId(null);
    await updateNotif({ status: 'read', readAt: serverTimestamp(), read: true });
    if (ctaUrl) navigate(ctaUrl);
  }

  function handleLater() {
    if (active) laterHiddenRef.current.add(active.id);
    setActiveId(null);
  }

  if (!user || !active || isModalBlocking) return null;

  return createPortal(
    <div className="notif-toast-host">
      <NotificationCard
        notification={active}
        onDismiss={handleDismiss}
        onCta={handleCta}
        onLater={handleLater}
      />
    </div>,
    document.body
  );
}
